//hook
import { UserConsumer } from '../../hooks/context/UserContext';
import { NotificationConsumer } from '../../hooks/context/NotificationContext';
import { useApi } from '../../hooks/useApi';

//type
import { clientProfileType } from '../../api/completedRole/type/CompletedRoleType';
import { completedClientReqType } from '../../api/completedRole/type/CompletedRoleType';

//api
import { PostCompletedRoleApi } from '../../api/completedRole/PostCompletedRoleApi';
import { GetProfileApi } from '../../api/profile/GetProfileApi';
import { UpdateCompletedRoleApi } from '../../api/completedRole/UpdateCompletedRoleApi';

//formik and yap
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';

//mui
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { styled } from '@mui/material/styles';

//translate
import { useTranslation } from 'react-i18next';
import i18n from '../../translate/i18n';

//utils
import { convertUrlToFile } from '../../utils/convertUrlToFile';

interface clientFieldProps {
    setOpenCompletedProfile: (open: boolean) => void
    RoleLicenseFront: string,
    setRoleLicenseFront: (RoleLicenseFront: string) => void
    RoleLicenseBack: string,
    setRoleLicenseBack: (RoleLicenseBack: string) => void
    clientProfile: clientProfileType
    setClientProfile:(clientProfile: clientProfileType) => void
    themeRole: string
}

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const storageURL = process.env.REACT_APP_STORAGE_BASE_URL;

const CompletedClientField = (
    {
    setOpenCompletedProfile, 
    RoleLicenseFront, 
    setRoleLicenseFront, 
    RoleLicenseBack, 
    setRoleLicenseBack, 
    clientProfile, 
    setClientProfile,
    themeRole
    }: clientFieldProps,
) => {
    const { t } = useTranslation();
    let {userData,setUserData} = UserConsumer()

    const {
        loading: postCompletedRoleLoading,
        error: postCompletedRoleError,
        HandlerApi: postCompletedRoleApi
    } = useApi(PostCompletedRoleApi, "Post");
    const {
        loading: updateCompletedRoleLoading,
        error: updateCompletedRoleError,
        HandlerApi: updateCompletedRoleApi
    } = useApi(UpdateCompletedRoleApi, "Post");
    
    //validation
    const cleintSchema = Yup.object().shape({
        billing_address: Yup.string().required(t("billing_address_required")),
        license_number: Yup.string().required(t("license_number_required")),
        front_image_url: Yup.mixed()
            .required(t("front_image_required"))
            .test("fileType", t("invalid_file_type"), (value) => {
            if (!value) return false;
            if (typeof value === "string") return true;
            return ["image/jpeg", "image/png", "image/jpg"].includes((value as File).type);
        }),
        background_image_url: Yup.mixed()
            .required(t("background_image_required"))
            .test("fileType", t("invalid_file_type"), (value) => {
            if (!value) return false;
            if (typeof value === "string") return true;
            return ["image/jpeg", "image/png", "image/jpg"].includes((value as File).type);
        }),
    })

    // start functions

    const handleSubmit = async (values: completedClientReqType, theme:string)=> {
        if ('front_image_url' in values && typeof values.front_image_url === "string") {
            values.front_image_url = await convertUrlToFile(values.front_image_url);
        }
        if ('background_image_url' in values && typeof values.background_image_url === "string") {
            values.background_image_url = await convertUrlToFile(values.background_image_url);
        }
        let result:any = ''
        if(userData?.is_client) {
            result = await updateCompletedRoleApi(theme, values, clientProfile?.id);
        }
        else {
            result = await postCompletedRoleApi(theme, values);
        }
        if(result.code == 200 || result.code == 201) {
            setRoleLicenseFront("")
            setRoleLicenseBack("")
            setOpenCompletedProfile(false)
            const resultprofile = await GetProfileApi();
            setUserData(resultprofile.data);
            setClientProfile({   
                id: 0,
                billing_address: "",
                license_number: "",
                front_image_url: null,
                background_image_url: null,
            })
        }
    }

    // end functions

    return (
        <div>
            <Formik
            enableReinitialize
            initialValues={{
                billing_address: clientProfile?.billing_address ?? "",
                license_number: clientProfile?.license_number ?? "",
                front_image_url: clientProfile?.front_image_url ?? null,
                background_image_url: clientProfile?.background_image_url ?? null,
            }}
            validationSchema={cleintSchema}
            onSubmit={(values)=>handleSubmit(values, themeRole)}
            >
            {({ values,touched, errors,setFieldValue }) => (
                <Form>
                    <div className="mt-5 flex max-sm:flex-col gap-2">
                        <Field
                            as={TextField}
                            name="billing_address"
                            label={t("billing_address")}
                            className="sm:w-1/2 w-full"
                            helperText={touched.billing_address && errors.billing_address ? errors.billing_address : ""}
                            error={touched.billing_address && Boolean(errors.billing_address)}
                        />
                        <Field
                            as={TextField}
                            name="license_number"
                            label={t("license_number")}
                            className="sm:w-1/2 w-full"
                            helperText={touched.license_number && errors.license_number ? errors.license_number : ""}
                            error={touched.license_number && Boolean(errors.license_number)}
                        />
                    </div>
                    <div className="mt-5 flex max-sm:flex-col gap-5">
                        <div>
                            <Button
                                component="label"
                                className='!bg-customerColor !text-white w-fit !capitalize'
                                startIcon={<CloudUploadIcon />}
                            >
                                {t("front_side")}
                                <VisuallyHiddenInput
                                type="file"
                                accept="image/*"
                                onChange={(event) => {
                                    const file = event.target.files?.[0];
                                    if (file) {
                                    setFieldValue("front_image_url", file);
                                    setRoleLicenseFront(URL.createObjectURL(file));
                                    }
                                }}
                                />
                            </Button>
                            <div>
                                {/* Show selected image preview */}
                                {RoleLicenseFront
                                    ? (
                                        <img
                                        src={RoleLicenseFront}
                                        alt="Front license preview"
                                        className="mt-3 w-40 h-28 object-cover rounded-lg border"
                                        loading="lazy"
                                        decoding="async"
                                        />
                                    ) 
                                    : userData?.is_client && clientProfile?.front_image_url ? (
                                        // Show existing uploaded image from backend
                                        <img
                                        src={`${storageURL}/${clientProfile.front_image_url}`}
                                        alt="Front license uploaded"
                                        className="mt-3 w-40 h-28 object-cover rounded-lg border"
                                        loading="lazy"
                                        decoding="async"
                                        />
                                    ) : null
                                }
                                <p className="text-[12px] mt-2 text-red-500">
                                    {touched.front_image_url && errors.front_image_url ? errors.front_image_url : ""}
                                </p>
                            </div>
                        </div>

                        <div>
                            <Button
                                component="label"
                                className='!bg-customerColor !text-white w-fit !capitalize'
                                startIcon={<CloudUploadIcon />}
                            >
                                {t("back_side")}
                                <VisuallyHiddenInput
                                type="file"
                                accept="image/*"
                                onChange={(event) => {
                                    const file = event.target.files?.[0];
                                    if (file) {
                                    setFieldValue("background_image_url", file);
                                    setRoleLicenseBack(URL.createObjectURL(file));
                                    }
                                }}
                                />
                            </Button>
                            {/* Show selected image preview */}
                            {RoleLicenseBack 
                                ? (
                                    <img
                                    src={RoleLicenseBack}
                                    alt="Back license preview"
                                    className="mt-3 w-40 h-28 object-cover rounded-lg border"
                                    loading="lazy"
                                    decoding="async"
                                    />
                                ) 
                                : userData?.is_client && clientProfile?.background_image_url ? (
                                    // Show existing uploaded image from backend
                                    <img
                                    src={`${storageURL}/${clientProfile.background_image_url}`}
                                    alt="Back license uploaded"
                                    className="mt-3 w-40 h-28 object-cover rounded-lg border"
                                    loading="lazy"
                                    decoding="async"
                                    />
                                ) : null
                            }
                            <p className="text-[12px] mt-2 text-red-500">
                                {touched.background_image_url && errors.background_image_url ? errors.background_image_url : ""}
                            </p>
                        </div>
                    </div>
                    <hr className='mt-5'/>
                    <DialogActions className={i18n.language === "en" ? "!justify-start" : "!justify-start"}>
                        <Button className='w-full !bg-customerColor !text-white !rounded-full' type="submit">
                            {postCompletedRoleLoading || updateCompletedRoleLoading
                            ? <CircularProgress className='w-1 h-1'/>
                            :userData?.is_client ? t("update") : t("confirm")
                            }
                        </Button>
                    </DialogActions>
                </Form>
            )}
            </Formik>
        </div>
    )
}

export default CompletedClientField